import { Node, Edge } from '@xyflow/react';
import { FlowStep, BranchStep, SplitStep, InsertableType, computeDayOffsets } from './useAutomationFlow';

// ─── Layout constants ─────────────────────────────────────────────────────────

const NODE_W = 260;
const ADD_W = 32;
const MAIN_X = 0;
const BRANCH_OFFSET = 250;
const NESTED_BRANCH_OFFSET = 160; // tighter offset for splits inside branches
const YES_X = MAIN_X - BRANCH_OFFSET;
const NO_X = MAIN_X + BRANCH_OFFSET;

const Y_STEP = 170;
const ADD_Y_OFFSET = 110;
const BRANCH_START_Y = 160;

// ─── Callback types ───────────────────────────────────────────────────────────

export type OnInsertMain = (afterStepId: string, type: InsertableType) => void;
export type OnInsertBranch = (
    splitId: string,
    branch: 'yes' | 'no',
    afterBranchStepId: string | null,
    type: InsertableType
) => void;
export type OnRemove = (stepId: string) => void;
export type OnFlipSplit = (stepId: string) => void;
export type OnStepClick = (stepId: string) => void;

// ─── Main layout entry point ──────────────────────────────────────────────────

export function canvasLayout(
    steps: FlowStep[],
    onInsertMain: OnInsertMain,
    onInsertBranch: OnInsertBranch,
    onRemove: OnRemove,
    onStepClick?: OnStepClick,
    onFlipSplit?: OnFlipSplit,
    readOnly?: boolean
): { nodes: Node[]; edges: Edge[] } {
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    let y = 0;
    let prevAddId: string | null = null;
    let prevStepId: string | null = null;

    for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        const isLast = i === steps.length - 1;

        nodes.push(stepNode(step, MAIN_X, y, onRemove, onStepClick, onFlipSplit, readOnly));

        if (readOnly) {
            if (prevStepId) edges.push(vEdge(`e-${prevStepId}-${step.id}`, prevStepId, step.id));
        } else if (prevAddId) {
            edges.push(vEdge(`e-${prevAddId}-${step.id}`, prevAddId, step.id));
        }

        if (step.type === 'split') {
            const s = step as SplitStep;
            const branchY = y + BRANCH_START_Y;

            const yes = buildBranch(
                step.id,
                'yes',
                s.branches[0].steps,
                YES_X,
                branchY,
                NESTED_BRANCH_OFFSET,
                onInsertBranch,
                onRemove,
                onStepClick,
                onFlipSplit,
                readOnly
            );
            const no = buildBranch(
                step.id,
                'no',
                s.branches[1].steps,
                NO_X,
                branchY,
                NESTED_BRANCH_OFFSET,
                onInsertBranch,
                onRemove,
                onStepClick,
                onFlipSplit,
                readOnly
            );

            nodes.push(...yes.nodes, ...no.nodes);
            edges.push(...yes.edges, ...no.edges);

            edges.push(branchEdge(`e-yes-${step.id}`, step.id, 'yes', yes.entryId, 'yes'));
            edges.push(branchEdge(`e-no-${step.id}`, step.id, 'no', no.entryId, 'no'));
            break;
        }

        if (readOnly) {
            if (isLast) {
                const endId = `end-after-${step.id}`;
                nodes.push(addNode(endId, MAIN_X, y + ADD_Y_OFFSET, () => undefined, true, false, true));
                edges.push(vEdge(`e-${step.id}-${endId}`, step.id, endId));
            }
            prevStepId = step.id;
        } else {
            const addId = `add-after-${step.id}`;
            nodes.push(addNode(addId, MAIN_X, y + ADD_Y_OFFSET, type => onInsertMain(step.id, type), isLast, false));
            edges.push(vEdge(`e-${step.id}-${addId}`, step.id, addId));
            prevAddId = addId;
        }

        y += Y_STEP;
    }

    // A pass over the finished nodes: buildBranch already carries eleven
    // arguments, and the day depends on delays across nested branches.
    const dayOffsets = computeDayOffsets(steps);

    return { nodes: nodes.map(node => withDayOffset(node, dayOffsets)), edges };
}

function withDayOffset(node: Node, dayOffsets: Map<string, number>): Node {
    const day = dayOffsets.get(node.id);
    if (day === undefined) return node;

    return { ...node, data: { ...node.data, dayOffset: day } };
}

// ─── Branch layout (recursive) ────────────────────────────────────────────────

function buildBranch(
    splitId: string,
    branch: 'yes' | 'no',
    branchSteps: BranchStep[],
    centerX: number,
    startY: number,
    nestedOffset: number,
    onInsertBranch: OnInsertBranch,
    onRemove: OnRemove,
    onStepClick?: OnStepClick,
    onFlipSplit?: OnFlipSplit,
    readOnly?: boolean
): { nodes: Node[]; edges: Edge[]; entryId: string } {
    const nodes: Node[] = [];
    const edges: Edge[] = [];

    if (branchSteps.length === 0) {
        const termId = `add-${branch}-empty-${splitId}`;
        nodes.push(
            addNode(termId, centerX, startY, type => onInsertBranch(splitId, branch, null, type), true, true, readOnly)
        );
        return { nodes, edges, entryId: termId };
    }

    let y = startY;
    let prevAddId: string | null = null;
    let prevStepId: string | null = null;

    for (let i = 0; i < branchSteps.length; i++) {
        const step = branchSteps[i];
        const isLast = i === branchSteps.length - 1;

        nodes.push(stepNode(step as FlowStep, centerX, y, onRemove, onStepClick, onFlipSplit, readOnly));

        if (readOnly) {
            if (prevStepId) edges.push(vEdge(`e-${prevStepId}-${step.id}`, prevStepId, step.id));
        } else if (prevAddId) {
            edges.push(vEdge(`e-${prevAddId}-${step.id}`, prevAddId, step.id));
        }

        // Nested split: recursively render its own yes/no branches
        if (step.type === 'split') {
            const s = step as SplitStep;
            const subBranchY = y + BRANCH_START_Y;
            const subOffset = Math.max(nestedOffset - 80, 120);

            const yes = buildBranch(
                step.id,
                'yes',
                s.branches[0].steps,
                centerX - nestedOffset,
                subBranchY,
                subOffset,
                onInsertBranch,
                onRemove,
                onStepClick,
                onFlipSplit,
                readOnly
            );
            const no = buildBranch(
                step.id,
                'no',
                s.branches[1].steps,
                centerX + nestedOffset,
                subBranchY,
                subOffset,
                onInsertBranch,
                onRemove,
                onStepClick,
                onFlipSplit,
                readOnly
            );

            nodes.push(...yes.nodes, ...no.nodes);
            edges.push(...yes.edges, ...no.edges);

            edges.push(branchEdge(`e-yes-${step.id}`, step.id, 'yes', yes.entryId, 'yes'));
            edges.push(branchEdge(`e-no-${step.id}`, step.id, 'no', no.entryId, 'no'));
            break; // nested split is terminal within this branch level
        }

        if (readOnly) {
            if (isLast) {
                const endId = `end-${branch}-after-${step.id}`;
                nodes.push(addNode(endId, centerX, y + ADD_Y_OFFSET, () => undefined, true, true, true));
                edges.push(vEdge(`e-${step.id}-${endId}`, step.id, endId));
            }
            prevStepId = step.id;
        } else {
            const addId = `add-${branch}-after-${step.id}`;
            nodes.push(
                addNode(
                    addId,
                    centerX,
                    y + ADD_Y_OFFSET,
                    type => onInsertBranch(splitId, branch, step.id, type),
                    isLast,
                    true
                )
            );
            edges.push(vEdge(`e-${step.id}-${addId}`, step.id, addId));
            prevAddId = addId;
        }

        y += Y_STEP;
    }

    return { nodes, edges, entryId: branchSteps[0].id };
}

// ─── Node factories ───────────────────────────────────────────────────────────

function stepNode(
    step: FlowStep,
    centerX: number,
    y: number,
    onRemove: OnRemove,
    onStepClick?: OnStepClick,
    onFlipSplit?: OnFlipSplit,
    readOnly?: boolean
): Node {
    return {
        id: step.id,
        type: stepTypeToRFType(step.type),
        position: { x: centerX - NODE_W / 2, y },
        data: {
            ...step,
            onRemove: !readOnly && step.type !== 'trigger' ? () => onRemove(step.id) : undefined,
            onStepClick: !readOnly && onStepClick ? () => onStepClick(step.id) : undefined,
            onFlipSplit: !readOnly && step.type === 'split' && onFlipSplit ? () => onFlipSplit(step.id) : undefined
        },
        draggable: false,
        selectable: !readOnly
    };
}

function addNode(
    id: string,
    centerX: number,
    y: number,
    onInsert: (type: InsertableType) => void,
    isTerminal: boolean,
    isBranch: boolean,
    readOnly?: boolean
): Node {
    return {
        id,
        type: 'add',
        position: { x: centerX - ADD_W / 2, y },
        data: { isTerminal, isBranch, onInsert, readOnly },
        draggable: false,
        selectable: false
    };
}

function stepTypeToRFType(type: string): string {
    switch (type) {
        case 'delay':
            return 'delay';
        case 'split':
            return 'split';
        case 'sms':
        case 'email':
            return 'action';
        default:
            return 'trigger';
    }
}

// ─── Edge factories ───────────────────────────────────────────────────────────

function vEdge(id: string, source: string, target: string): Edge {
    return {
        id,
        source,
        target,
        type: 'straight',
        style: { stroke: '#d1d5db', strokeWidth: 2 }
    };
}

function branchEdge(id: string, source: string, sourceHandle: string, target: string, branch: 'yes' | 'no'): Edge {
    return {
        id,
        source,
        sourceHandle,
        target,
        type: 'branch',
        data: { branch },
        style: { stroke: '#d1d5db', strokeWidth: 2 }
    };
}
